import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, ChevronRight, ChevronUp, ChevronDown, X, Loader2, Copy, CheckCircle2, Trash2, ExternalLink, Download, Eye, Plus } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_SUBS = [
  { category: "Access Control", subs: [
    { id: "atlas", title: "Atlas Series" },
    { id: "biocv", title: "Bio CVSecurity" },
    { id: "armatura", title: "Armatura One" },
  ]},
  { category: "Pedestrian Entrance Control", subs: [
    { id: "speedlanes", title: "Speedlane Series" },
    { id: "turnstiles", title: "Turnstiles" },
    { id: "gates", title: "Swing Gates" },
    { id: "fht-perimeter", title: "Full Height Turnstiles" },
  ]},
  { category: "Time & Attendance", subs: [
    { id: "biometric-terminals", title: "Biometric Terminals" },
    { id: "software", title: "Software Solutions" },
  ]},
  { category: "Visitor Management", subs: [
    { id: "vm-terminals", title: "Visitor Terminals" },
    { id: "vm-software", title: "VM Software" },
  ]},
  { category: "Security Inspection", subs: [
    { id: "walkthrough", title: "Walk-Through Detectors" },
    { id: "xray", title: "X-Ray Inspection" },
  ]},
  { category: "Perimeter Protection", subs: [
    { id: "road-blocker", title: "Road Blocker" },
    { id: "rising-bollards", title: "Rising Bollards" },
    { id: "barriers", title: "Parking Barriers" },
    { id: "anpr", title: "ANPR Camera" },
    { id: "tyre-killer", title: "Tyre Killer" },
    { id: "uvis", title: "Under Vehicle Inspection System" },
  ]},
  { category: "AI & Analytics", subs: [
    { id: "people-counting", title: "People Counting" },
    { id: "facial-analytics", title: "Facial Analytics" },
    { id: "behaviour", title: "Behaviour Detection" },
  ]},
];

// Lookup maps derived from CATEGORY_SUBS
const SUBS_BY_CAT = Object.fromEntries(CATEGORY_SUBS.map((c) => [c.category, c.subs]));
const SUB_TITLE = Object.fromEntries(CATEGORY_SUBS.flatMap((c) => c.subs.map((s) => [s.id, s.title])));

export default function CustomerPresentation({ onBack }) {
  const [view, setView] = useState("list"); // "list", "create"
  const [presentations, setPresentations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState("");

  // Create form
  const [customerName, setCustomerName] = useState("");
  const [customerCompany, setCustomerCompany] = useState("");
  const [presentationTitle, setPresentationTitle] = useState("");
  const [sections, setSections] = useState([]); // [{ category, subs: [subId,...] }] in slide order
  const [expandedCat, setExpandedCat] = useState(null); // single open accordion
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return { Authorization: `Bearer ${token}` };
  };

  useEffect(() => { fetchPresentations(); }, []);

  const fetchPresentations = async () => {
    try {
      const res = await axios.get(`${API}/presentations/list`, { headers: getAuthHeaders() });
      setPresentations(res.data || []);
    } catch (err) {
      console.error("Failed to fetch presentations:", err);
    } finally {
      setLoading(false);
    }
  };

  const isCatActive = (cat) => sections.some((s) => s.category === cat);

  const selectCategory = (cat) => {
    if (isCatActive(cat)) {
      setExpandedCat(expandedCat === cat ? null : cat);
    } else {
      setSections((p) => [...p, { category: cat, subs: [] }]);
      setExpandedCat(cat);
    }
  };

  const removeCategory = (cat) => {
    setSections((p) => p.filter((s) => s.category !== cat));
    if (expandedCat === cat) setExpandedCat(null);
  };

  const toggleSub = (cat, subId) => {
    if (!isCatActive(cat)) setExpandedCat(cat);
    setSections((p) => {
      const exists = p.some((s) => s.category === cat);
      const base = exists ? p : [...p, { category: cat, subs: [] }];
      return base.map((s) => {
        if (s.category !== cat) return s;
        const has = s.subs.includes(subId);
        return { ...s, subs: has ? s.subs.filter((x) => x !== subId) : [...s.subs, subId] };
      });
    });
  };

  const moveCategory = (cat, dir) => {
    setSections((p) => {
      const i = p.findIndex((s) => s.category === cat); const j = i + dir;
      if (i < 0 || j < 0 || j >= p.length) return p;
      return p.map((v, idx) => (idx === i ? p[j] : idx === j ? p[i] : v));
    });
  };

  const moveSub = (cat, subId, dir) => {
    setSections((p) => p.map((s) => {
      if (s.category !== cat) return s;
      const i = s.subs.indexOf(subId); const j = i + dir;
      if (i < 0 || j < 0 || j >= s.subs.length) return s;
      const subs = s.subs.map((v, idx) => (idx === i ? s.subs[j] : idx === j ? s.subs[i] : v));
      return { ...s, subs };
    }));
  };

  const selectedCount = sections.reduce((a, s) => a + s.subs.length, 0);
  const orderedSubcategories = () => sections.flatMap((s) => s.subs);

  const handleCreate = async () => {
    if (!customerCompany.trim()) { setError("Customer company is required."); return; }
    if (selectedCount === 0) { setError("Select at least one product area."); return; }
    setError("");
    setCreating(true);
    try {
      const subcategories = orderedSubcategories();
      await axios.post(`${API}/presentations/create`, {
        customer_name: customerName.trim(),
        customer_company: customerCompany.trim(),
        title: presentationTitle.trim(),
        subcategories,
      }, { headers: getAuthHeaders() });
      setCustomerName("");
      setCustomerCompany("");
      setPresentationTitle("");
      setSections([]);
      setExpandedCat(null);
      setView("list");
      await fetchPresentations();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to create.");
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this presentation?")) return;
    try {
      await axios.delete(`${API}/presentations/${id}`, { headers: getAuthHeaders() });
      setPresentations((prev) => prev.filter((p) => p.id !== id));
    } catch (err) {
      console.error("Delete failed:", err);
    }
  };

  const copyLink = (id) => {
    navigator.clipboard.writeText(`https://www.zkuk-config.co.uk/presentation/view/${id}`);
    setCopied(id);
    setTimeout(() => setCopied(""), 2000);
  };

  if (view === "create") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center gap-4 mb-6">
            <Button variant="ghost" size="sm" onClick={() => setView("list")} className="text-slate-600 hover:text-slate-900">
              <ArrowLeft className="h-4 w-4 mr-1" /> Back
            </Button>
          </div>

          <h1 className="text-2xl font-bold text-slate-900 mb-6" data-testid="create-pres-title">Create Customer Presentation</h1>

          {/* Customer Info */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-6">
            <h2 className="text-base font-bold text-slate-800 mb-4">Customer Details</h2>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label className="text-sm text-slate-600">Contact Name</Label>
                <Input value={customerName} onChange={(e) => setCustomerName(e.target.value)} placeholder="e.g. John Smith" className="mt-1" data-testid="pres-customer-name" />
              </div>
              <div>
                <Label className="text-sm text-slate-600">Company Name *</Label>
                <Input value={customerCompany} onChange={(e) => setCustomerCompany(e.target.value)} placeholder="e.g. ABC Corporation" className="mt-1" data-testid="pres-customer-company" />
              </div>
            </div>
            <div className="mt-4">
              <Label className="text-sm text-slate-600">Presentation Title (optional)</Label>
              <Input value={presentationTitle} onChange={(e) => setPresentationTitle(e.target.value)} placeholder="e.g. Security Solution for ABC Corp" className="mt-1" data-testid="pres-title-input" />
            </div>
          </div>

          {/* Product Selection — ordered slide builder */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-6">
            <div className="flex items-center justify-between mb-1">
              <h2 className="text-base font-bold text-slate-800">Build Your Slide Order</h2>
              <span className="text-sm text-slate-500">{selectedCount} slide{selectedCount === 1 ? "" : "s"}</span>
            </div>
            <p className="text-xs text-slate-500 mb-4">Click a product area to add it as the next section. Open it and tick products to add sub-slides. Use the arrows to re-order — the numbers show the order they&apos;ll be presented in.</p>

            {/* Ordered selected categories */}
            {sections.length > 0 && (
              <div className="space-y-3 mb-5">
                {sections.map((sec, ci) => {
                  const cat = sec.category;
                  const subs = SUBS_BY_CAT[cat] || [];
                  const sel = sec.subs;
                  const isOpen = expandedCat === cat;
                  return (
                    <div key={cat} className="border border-blue-200 rounded-lg overflow-hidden" data-testid={`ordered-cat-${cat}`}>
                      <div className="flex items-center justify-between px-3 py-2.5 bg-blue-50">
                        <button onClick={() => selectCategory(cat)} className="flex items-center gap-3 text-left flex-1 min-w-0" data-testid={`cat-toggle-${cat}`}>
                          <span className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center">{ci + 1}</span>
                          <span className="text-sm font-semibold text-slate-800 truncate">{cat}</span>
                          <ChevronRight className={`h-4 w-4 text-slate-400 transition-transform ${isOpen ? "rotate-90" : ""}`} />
                        </button>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <button onClick={() => moveCategory(cat, -1)} disabled={ci === 0} className="p-1 text-slate-400 hover:text-slate-700 disabled:opacity-30" data-testid={`cat-up-${cat}`}><ChevronUp className="h-4 w-4" /></button>
                          <button onClick={() => moveCategory(cat, 1)} disabled={ci === sections.length - 1} className="p-1 text-slate-400 hover:text-slate-700 disabled:opacity-30" data-testid={`cat-down-${cat}`}><ChevronDown className="h-4 w-4" /></button>
                          <button onClick={() => removeCategory(cat)} className="p-1 text-red-400 hover:text-red-600" data-testid={`cat-remove-${cat}`}><X className="h-4 w-4" /></button>
                        </div>
                      </div>
                      {isOpen && (
                        <div className="px-3 py-2 space-y-1">
                          {sel.map((subId, si) => (
                            <div key={subId} className="flex items-center justify-between gap-2 py-1.5 px-2 rounded bg-slate-50">
                              <button onClick={() => toggleSub(cat, subId)} className="flex items-center gap-2.5 text-left flex-1 min-w-0" data-testid={`remove-sub-${subId}`}>
                                <span className="flex-shrink-0 text-[11px] font-bold text-blue-700 bg-blue-100 rounded px-1.5 py-0.5">{ci + 1}.{si + 1}</span>
                                <span className="text-sm text-slate-700 truncate">{SUB_TITLE[subId]}</span>
                              </button>
                              <div className="flex items-center gap-1 flex-shrink-0">
                                <button onClick={() => moveSub(cat, subId, -1)} disabled={si === 0} className="p-0.5 text-slate-400 hover:text-slate-700 disabled:opacity-30" data-testid={`sub-up-${subId}`}><ChevronUp className="h-3.5 w-3.5" /></button>
                                <button onClick={() => moveSub(cat, subId, 1)} disabled={si === sel.length - 1} className="p-0.5 text-slate-400 hover:text-slate-700 disabled:opacity-30" data-testid={`sub-down-${subId}`}><ChevronDown className="h-3.5 w-3.5" /></button>
                                <button onClick={() => toggleSub(cat, subId)} className="p-0.5 text-red-400 hover:text-red-600"><X className="h-3.5 w-3.5" /></button>
                              </div>
                            </div>
                          ))}
                          {subs.filter((s) => !sel.includes(s.id)).map((s) => (
                            <button key={s.id} onClick={() => toggleSub(cat, s.id)} className="flex items-center gap-2.5 w-full text-left py-1.5 px-2 rounded hover:bg-slate-50" data-testid={`add-sub-${s.id}`}>
                              <Plus className="h-3.5 w-3.5 text-slate-400" />
                              <span className="text-sm text-slate-500">{s.title}</span>
                            </button>
                          ))}
                          {subs.length === 0 && <p className="text-xs text-slate-400 px-2 py-1">No products in this area.</p>}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Add a product area */}
            {CATEGORY_SUBS.filter((c) => !isCatActive(c.category)).length > 0 && (
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-2">Add a product area</p>
                <div className="flex flex-wrap gap-2">
                  {CATEGORY_SUBS.filter((c) => !isCatActive(c.category)).map((c) => (
                    <button key={c.category} onClick={() => selectCategory(c.category)} className="inline-flex items-center gap-1.5 text-sm border border-slate-200 rounded-lg px-3 py-2 hover:border-blue-400 hover:bg-blue-50 transition-colors" data-testid={`add-cat-${c.category}`}>
                      <Plus className="h-3.5 w-3.5 text-blue-500" /> {c.category}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {error && <p className="text-sm text-red-600 mb-4" data-testid="pres-error">{error}</p>}

          <Button onClick={handleCreate} disabled={creating} className="w-full h-11 text-base font-semibold bg-green-600 hover:bg-green-700" data-testid="pres-create-btn">
            {creating ? <Loader2 className="h-5 w-5 animate-spin mr-2" /> : null}
            {creating ? "Creating..." : "Create Presentation"}
          </Button>
        </div>
      </div>
    );
  }

  // List view
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" size="sm" onClick={onBack} className="text-slate-600 hover:text-slate-900" data-testid="cust-pres-back-btn">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>
        </div>

        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" data-testid="cust-pres-title">Customer Presentations</h1>
            <p className="text-sm text-slate-500 mt-1">Create tailored presentations for your customers.</p>
          </div>
          <Button onClick={() => setView("create")} className="bg-blue-600 hover:bg-blue-700" data-testid="new-pres-btn">
            <Plus className="h-4 w-4 mr-1" /> New Presentation
          </Button>
        </div>

        {loading ? (
          <p className="text-slate-500">Loading...</p>
        ) : presentations.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-lg border border-slate-200">
            <p className="text-slate-500 font-medium mb-3">No presentations created yet.</p>
            <Button onClick={() => setView("create")} variant="outline">Create Your First Presentation</Button>
          </div>
        ) : (
          <div className="space-y-3">
            {presentations.map((p) => (
              <div key={p.id} className="bg-white rounded-xl border border-slate-200 shadow-sm px-5 py-4" data-testid={`pres-card-${p.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-slate-900 truncate">{p.title}</p>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {p.customer_name ? `${p.customer_name}, ` : ""}{p.customer_company} &middot; {p.subcategories?.length || 0} products &middot; {p.created_at ? new Date(p.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : ""}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Button variant="outline" size="sm" className="text-xs h-8" onClick={() => copyLink(p.id)}>
                      {copied === p.id ? <CheckCircle2 className="h-3.5 w-3.5 mr-1 text-green-600" /> : <Copy className="h-3.5 w-3.5 mr-1" />}
                      {copied === p.id ? "Copied!" : "Copy Link"}
                    </Button>
                    <a href={`/presentation/view/${p.id}`} target="_blank" rel="noopener noreferrer">
                      <Button variant="outline" size="sm" className="text-xs h-8"><Eye className="h-3.5 w-3.5 mr-1" /> View</Button>
                    </a>
                    <a href={`${API}/presentations/${p.id}/pdf`} download>
                      <Button variant="outline" size="sm" className="text-xs h-8"><Download className="h-3.5 w-3.5 mr-1" /> PDF</Button>
                    </a>
                    <Button variant="outline" size="sm" className="text-xs h-8 text-red-500 hover:text-red-700 hover:bg-red-50 border-red-200" onClick={() => handleDelete(p.id)} data-testid={`delete-pres-${p.id}`}>
                      <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
